/**
 * English ⇄ Telugu switch — port of `appLangProvider` in the Flutter app's `app/preferences.dart`.
 * English is the default; the choice is remembered per browser. Dates and numbers stay in the
 * `en-IN` forms on both, as they do in the Flutter app.
 */
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Lang, Tr, setActiveLang, translate } from '../i18n/l10n';

const LANG_KEY = 'resto.lang';

interface LangValue {
    lang: Lang;
    isTelugu: boolean;
    toggle: () => void;
    setLang: (lang: Lang) => void;
    /** Looks a key up in a string table in the current language. Changes identity with the language. */
    tr: Tr;
}

const LangContext = createContext<LangValue | undefined>(undefined);

export const useLang = (): LangValue => {
    const value = useContext(LangContext);
    if (!value) throw new Error('useLang must be used within a LangProvider');
    return value;
};

/** `const tr = useTr(); tr(ownerStrings, 'save')` — re-renders when the language changes. */
export const useTr = (): Tr => useLang().tr;

const initialLang = (): Lang => {
    try {
        return localStorage.getItem(LANG_KEY) === 'te' ? 'te' : 'en';
    } catch {
        return 'en';
    }
};

export function LangProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState<Lang>(initialLang);
    setActiveLang(lang);

    useEffect(() => {
        document.documentElement.lang = lang;
        try {
            localStorage.setItem(LANG_KEY, lang);
        } catch {
            /* private mode */
        }
    }, [lang]);

    const setLang = useCallback((next: Lang) => setLangState(next), []);
    const toggle = useCallback(() => setLangState((l) => (l === 'en' ? 'te' : 'en')), []);
    const tr = useCallback<Tr>((table, key, args) => translate(lang, table, key, args), [lang]);

    const value = useMemo(
        () => ({ lang, isTelugu: lang === 'te', toggle, setLang, tr }),
        [lang, toggle, setLang, tr],
    );
    return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}
